import getArticles from "@/api/requests/wordpress/getArticles";
import getCategory from "@/api/requests/wordpress/getCategory";
import getMenuItems from "@/api/requests/wordpress/getMenuItems";
import getYoastRedirect from "@/api/requests/wordpress/getYoastRedirect";
import { ArticleExcerpt, MenuItems, Taxonomy } from "@/custom-types/Wordpress";
import { createGetServerSideProps } from "@/utils/createGetServerSideProps";
import { getUrlPath } from "@/utils/getUrlPath";
import { getCountryCodeHeader } from "@/utils/requestContext";

import ListLayout from "@/components/news/Layouts/ListLayout";
import PageLayout from "@/components/news/Layouts/PageLayout";

type CategoryPageProps = {
  menuItems: MenuItems;
  category: Taxonomy;
  articles: ArticleExcerpt[];
  totalCount: number;
  totalPages: number;
};

const CategoryPage: React.FC<CategoryPageProps> = ({
  category,
  articles,
  menuItems,
  totalCount,
  totalPages,
}) => {
  const breadcrumbs = [
    {
      href: "/news",
      title: "News",
    },
    {
      href: `/news/category/${category.slug}`,
      title: category.title,
    },
  ];

  return (
    <PageLayout
      menuItems={menuItems}
      page={{ ...category, breadcrumbs, categories: [category.slug] }}
      currentPage={1}
      totalPages={totalPages}
    >
      <ListLayout
        title={`Articles in ${category.title} category`}
        articles={articles}
        totalCount={totalCount}
        currentPage={1}
        totalPages={totalPages}
        slug={category.slug}
      />
    </PageLayout>
  );
};

export const getServerSideProps = createGetServerSideProps<
  CategoryPageProps,
  {
    categorySlug: string;
  }
>(async (context) => {
  const { resolvedUrl, params } = context;
  const redirect = await getYoastRedirect(getUrlPath(resolvedUrl));

  if (redirect?.destination) {
    return {
      redirect,
    };
  }

  if (!params?.categorySlug) {
    return {
      notFound: true,
    };
  }

  const countryCode = getCountryCodeHeader(context.req.headers);

  const category = await getCategory(params.categorySlug, countryCode);

  if (!category) {
    return {
      notFound: true,
    };
  }

  const [{ articles, totalCount, totalPages }, menuItems] = await Promise.all([
    getArticles(countryCode, {
      categories: `${category.id}`,
    }),
    getMenuItems(countryCode === "CA" ? "canada" : "global"),
  ]);

  return {
    props: {
      articles: articles as ArticleExcerpt[],
      category,
      menuItems,
      totalCount,
      totalPages,
    },
  };
});

export default CategoryPage;
